// src/components/LoginModal.jsx
import React, { useState, useEffect } from 'react';
import { FiX, FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import api from '../api';

// ✅ Helper to get or create device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    // Generate a unique device ID
    deviceId = 'dev_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('deviceId', deviceId);
    console.log('🆕 New device ID generated:', deviceId);
  }
  return deviceId;
};

const LoginModal = ({ 
  onClose, 
  handleSignIn, 
  email, 
  setEmail, 
  password, 
  setPassword, 
  loginError,
  theme,
  onLoginSuccess,
  buttonLoading: externalButtonLoading = false
}) => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordState, setForgotPasswordState] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [sessionMessage, setSessionMessage] = useState('');

  const urlReason = searchParams?.get('reason') || '';
  const urlSessionExpired = searchParams?.get('session_expired') || '';

  useEffect(() => {
    if (urlReason === 'session_expired') {
      setSessionMessage('Your session timed out. Please log in again.');
    } else if (urlReason === 'legacy_token') {
      setSessionMessage('Please log in again to update your account security.');
    } else if (urlReason === 'token_expired') {
      setSessionMessage('Your login token expired. Please log in again.');
    } else if (urlSessionExpired === 'logged_in_elsewhere') {
      setSessionMessage('You logged in on another device. Please log in here again.');
    } else {
      setSessionMessage('');
    }
    
    if (urlSessionExpired || urlReason) {
      localStorage.removeItem('tokenVersion');
      localStorage.removeItem('sessionId');
    }

    return () => {
      setSessionMessage('');
    };
  }, [urlReason, urlSessionExpired]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoading || externalButtonLoading) return;
    
    setIsLoading(true);
    
    try {
      // ✅ Get device ID and pass it to handleSignIn
      const deviceId = getDeviceId();
      
      // ✅ Call handleSignIn with deviceId as second parameter
      const response = await handleSignIn(e, deviceId);
      
      if (response?.data?.requiresOTP) {
        console.log('🔐 OTP required, parent component will handle');
        setIsLoading(false);
        return;
      }
      
      if (response?.data?.subscription) {
        const subscription = response.data.subscription;
        subscription.isTenantAdmin = subscription.isTenantAdmin || 
                                   response.data.user?.role === 'tenant_admin' || 
                                   response.data.user?.role === 'super_admin';
        
        localStorage.setItem('subscription', JSON.stringify(subscription));
        console.log('✅ [LoginModal] Subscription stored:', subscription);
      }
      
      if (response?.data?.user?.role) {
        localStorage.setItem('userRole', response.data.user.role);
      }
      
      if (response?.data?.tenant?.id) {
        localStorage.setItem('tenantId', response.data.tenant.id);
      }
      
      if (response?.data?.user?.id) {
        localStorage.setItem('userId', response.data.user.id);
      }
      
      if (response?.data?.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      if (response?.data?.sessionId) {
        localStorage.setItem('sessionId', response.data.sessionId);
      }
      
      if (response?.data?.tokenVersion !== undefined) {
        localStorage.setItem('tokenVersion', response.data.tokenVersion.toString());
      }
      
      setSessionMessage('');
      
      if (onLoginSuccess) {
        onLoginSuccess(response.data);
      }
      
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.response?.data?.requiresOTP) {
        console.log('🔐 OTP required, parent component will handle');
        throw error;
      }
      
      const errorCode = error.response?.data?.code;
      if (errorCode === 'TOKEN_VERSION_MISMATCH') {
        setSessionMessage('You logged in somewhere else. Please log in here again.');
        localStorage.clear();
      } else if (errorCode === 'LEGACY_TOKEN') {
        setSessionMessage('Please log in again to update your security settings.');
        localStorage.clear();
      } else if (errorCode === 'SESSION_INACTIVE') {
        setSessionMessage('Your session has expired or was terminated. Please log in again.');
        localStorage.clear();
      } else if (errorCode === 'ACTIVE_SESSIONS_EXIST') {
        setSessionMessage('You have active sessions on other devices. Please check your open sessions.');
      } else {
        setSessionMessage('');
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email || !email.trim()) {
      setForgotPasswordState({
        type: 'error',
        text: 'Please type your email address in the input box below first.'
      });
      return;
    }

    setIsLoading(true);
    setForgotPasswordState({ type: '', text: '' });
    
    try {
      const response = await api.post('/api/new-auth/forgot-password', {
        email: email.trim().toLowerCase()
      });

      if (response.data?.resetUrl) {
        setForgotPasswordState({
          type: 'success',
          text: 'Reset link created! Check the server logs to copy it.'
        });
      } else {
        setForgotPasswordState({
          type: 'success',
          text: 'If that email exists, we sent a password link to it.'
        });
      }
    } catch (error) {
      setForgotPasswordState({
        type: 'error',
        text: error.response?.data?.message || 'Could not send the password link.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSessionMessage('');
    setForgotPasswordState({ type: '', text: '' });
    onClose();
  };

  const isButtonLoading = isLoading || externalButtonLoading;

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in">
      
      <div className="absolute inset-0" onClick={() => !isButtonLoading && handleClose()} />

      <div className="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 z-10 max-h-[95vh] flex flex-col animate-slide-up">
        
        <div 
          className="h-1.5 w-full shrink-0"
          style={{ background: `linear-gradient(135deg, ${theme?.primaryColor || '#3b82f6'}, ${theme?.secondaryColor || '#6366f1'})` }}
        />

        <button
          onClick={handleClose}
          disabled={isButtonLoading}
          className="absolute right-4 top-5 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors z-20 disabled:opacity-40"
        >
          <FiX className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto p-6 sm:p-8 space-y-6">
          
          <div className="text-center sm:text-left mt-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Welcome Back</h2>
            <p className="text-sm text-gray-500 mt-1">Please enter your details to log into your account.</p>
          </div>

          {sessionMessage && (
            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm flex items-start gap-2.5">
              <FiAlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <span>{sessionMessage}</span>
            </div>
          )}

          {forgotPasswordState.text && (
            <div className={`p-3.5 border rounded-xl text-sm flex items-start gap-2.5 animate-fade-in ${
              forgotPasswordState.type === 'error' ? 'bg-red-50 border-red-100 text-red-800' : 'bg-green-50 border-green-100 text-green-800'
            }`}>
              {forgotPasswordState.type === 'error' ? (
                <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              ) : (
                <FiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              )}
              <span className="font-medium">{forgotPasswordState.text}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700">Email Address</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600">
                  <FiMail className="h-5 w-5" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isButtonLoading}
                  className="block w-full pl-11 pr-3 py-3 text-sm bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:opacity-50 hover:border-gray-300"
                  placeholder="name@example.com"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700">Password</label>
                <button
                  type="button"
                  disabled={isButtonLoading}
                  onClick={handleForgotPassword}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600">
                  <FiLock className="h-5 w-5" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isButtonLoading}
                  className="block w-full pl-11 pr-11 py-3 text-sm bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:opacity-50 hover:border-gray-300"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  disabled={isButtonLoading}
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {loginError && (
              <div className="p-3.5 bg-red-50 border border-red-100 text-red-800 rounded-xl flex items-start gap-2.5 text-sm animate-shake">
                <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="font-medium">{loginError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isButtonLoading}
              className="w-full mt-2 py-3 px-4 rounded-xl font-semibold text-sm text-white shadow-md hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 relative overflow-hidden group"
              style={{ background: `linear-gradient(135deg, ${theme?.primaryColor || '#3b82f6'}, ${theme?.secondaryColor || '#6366f1'})` }}
            >
              <span className={isButtonLoading ? 'opacity-0' : 'opacity-100'}>
                Sign In
              </span>
              {isButtonLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </button>
          </form>

          <div className="text-center text-xs text-gray-400 pt-2">
            Secure login with device verification
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-4px); }
          40%, 80% { transform: translateX(4px); }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @media (min-width: 640px) {
          @keyframes slideUp { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        }
        .animate-shake { animation: shake 0.4s ease-in-out; }
        .animate-fade-in { animation: fadeIn 0.15s linear; }
        .animate-slide-up { animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
};

export default LoginModal;